import { useState, useEffect, useCallback, useRef } from 'react'

type UseLocalStorageKeyDirectOptions = {
  /**
   * If true, reads from localStorage into a ref only (not React state).
   * This avoids an unnecessary re-render when the data is not needed for rendering
   * (e.g., Apollo already has data). The cached value is still accessible via dataRef
   * for comparison before writing.
   */
  deferLoad?: boolean
  /**
   * If true, skips loading from localStorage entirely.
   * Returns immediately with loading: false and data: undefined.
   */
  skip?: boolean
  /**
   * Maximum size in bytes for a single cache entry.
   * If the serialized value exceeds this, the write is silently skipped.
   */
  maxEntryBytes?: number
}

/**
 * A hook to use a key from localStorage with direct state management.
 *
 * Similar to useLocalForageKeyDirect but uses localStorage instead of IndexedDB:
 * - Synchronous reads (faster initial load, but blocks main thread briefly)
 * - 5MB total storage limit (shared across all keys)
 * - Data persists across sessions
 * - Updates state immediately on setData (optimistic update)
 * - Does not share state across components (each instance has its own state)
 *
 * Use this for smaller, frequently accessed cache entries where synchronous
 * access is preferred over IndexedDB's async nature.
 *
 * @param key - The key to read/write in localStorage
 * @param options - Optional configuration
 */
export const useLocalStorageKeyDirect = <T extends {}>(
  key: string,
  options?: UseLocalStorageKeyDirectOptions
) => {
  const [data, setDataState] = useState<T | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const cachedRef = useRef<T | undefined>(undefined)

  const skip = options?.skip ?? false

  // Read from localStorage on mount (or when key changes)
  useEffect(() => {
    // Skip loading entirely if skip option is enabled
    if (skip) {
      cachedRef.current = undefined
      setDataState(undefined)
      setLoading(false)
      return
    }

    // deferLoad is intentionally not in deps - we only want to check it at mount/key-change,
    // not re-trigger loading if it changes mid-flight (e.g., Apollo returns data)
    const shouldDefer = options?.deferLoad ?? false

    try {
      const raw = localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw) as T
        cachedRef.current = parsed
        // Only update React state when not deferring — avoids re-render when
        // the caller already has fresh data and only needs the ref for comparison.
        if (!shouldDefer) setDataState(parsed)
      } else {
        cachedRef.current = undefined
        if (!shouldDefer) setDataState(undefined)
      }
    } catch (err) {
      console.error('[localStorage] Failed to read/parse cache:', err)
      cachedRef.current = undefined
      if (!shouldDefer) setDataState(undefined)
    }
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skip])

  const maxEntryBytes = options?.maxEntryBytes

  // Write to localStorage and keep ref in sync
  const setData = useCallback(
    (value: T) => {
      try {
        const serialized = JSON.stringify(value)

        // Skip write if entry exceeds size limit
        if (maxEntryBytes && new Blob([serialized]).size > maxEntryBytes) {
          return
        }

        localStorage.setItem(key, serialized)
        cachedRef.current = value
      } catch (err) {
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
          console.warn('[localStorage] Storage quota exceeded, cache write failed for key:', key)
        } else {
          console.error('[localStorage] Failed to write cache:', err)
        }
      }
    },
    [key, maxEntryBytes]
  )

  return {
    loading,
    data,
    setData,
    /** Ref holding the latest cached value. Always populated (even when deferLoad is true). */
    dataRef: cachedRef,
    hasKey: !loading ? Boolean(cachedRef.current) : undefined,
  }
}
